import React, { useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormFieldLayout,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ClientDto } from 'api/Clients/Types/Client'
import { ComplexTransfer } from 'api/Transaction/Types/ComplexTransfer'
import { TransactionDto } from 'api/Transaction/Types/Transaction'
import { TransactionDirectionType } from 'api/Transaction/Types/TransactionDirectionType'
import { TransactionTypeDto } from 'api/Wallet/Types/TransactionTypeDto'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

export interface AdjustmentFormValues {
  client: string | null
  walletId: string
  amount: number
  comment: string
  directionTypeId: string
  transactionTypeId: number
  currency: string | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | null
}

function dataToSubmit(values: AdjustmentFormValues): FormSubmitValues<ComplexTransfer> {
  return {
    transactionTypeId: values.transactionTypeId,
    transactions: [
      {
        walletId: values.walletId,
        amount: values.amount,
        directionTypeId: values.directionTypeId,
      },
    ],
    comment: values.comment,
  }
}

const AdjustmentWalletFormUI: React.FC<FormikProps<AdjustmentFormValues> & OuterProps> = (
  props
) => {
  const { client, handleSubmit, isSubmitting, values, errors } = props
  const transactionTypeOptions = props.transactionTypes.map((transactionType) => ({
    id: transactionType.id,
    name: transactionType.name ?? '',
  }))

  useEffect(() => {
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectedTransactionType = props.transactionTypes.find(
    (item) => item.id === Number(values.transactionTypeId)
  )
  const isDirectionLocked = Boolean(selectedTransactionType?.directionType)

  return (
    <Form onSubmit={handleSubmit}>
      <DataGrid columns={1}>
        <DataColumn>
          <DataCard title='Wallet Adjustment' columns={2}>
            <FormReadonlyField
              name='client'
              label='Client'
              value={values.client ?? ''}
              rightAddon={
                <>
                  <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                    Select
                  </Button>
                  {client?.id && (
                    <Link
                      to={generatePath(links.recordsClients.detailsUrl as string, {
                        id: client.id,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )}
                </>
              }
            />
            <FormSelectField
              name='walletId'
              label='Wallet'
              value={values.walletId || undefined}
              options={props.wallets.map((wallet) => ({
                id: wallet.id,
                name: `${wallet.name}-balance:${wallet.balance}`,
              }))}
              error={errors.walletId}
              onChange={(value) => {
                const nextWalletId = value ? String(value) : ''
                props.setFieldValue('walletId', nextWalletId)
                props.setWallet(nextWalletId)
              }}
              rightAddon={
                values.walletId && (
                  <Link
                    to={generatePath(links.recordsWallets.detailsUrl as string, {
                      id: values.walletId,
                      slug: 'details',
                    })}
                    showIcon
                  />
                )
              }
            />
            <FormSelectField
              name='transactionTypeId'
              label='Transaction Type'
              value={values.transactionTypeId || undefined}
              options={transactionTypeOptions}
              error={errors.transactionTypeId}
              onChange={(value) => {
                const nextTransactionType = props.transactionTypes.find(
                  (item) => item.id === Number(value)
                )

                if (!nextTransactionType) {
                  return
                }

                props.setFieldValue('transactionTypeId', nextTransactionType.id)

                if (nextTransactionType.directionType) {
                  props.setFieldValue(
                    'directionTypeId',
                    nextTransactionType.directionType.id.toString()
                  )
                }
              }}
            />
            <FormSelectField
              name='directionTypeId'
              label='Debit/Credit'
              value={values.directionTypeId || undefined}
              options={props.directionTypes.map((directionType) => ({
                id: directionType.id.toString(),
                name: directionType.name,
              }))}
              error={errors.directionTypeId}
              disabled={isDirectionLocked}
              onChange={(value) => props.setFieldValue('directionTypeId', String(value))}
            />
            <FormField
              name='amount'
              label='Amount'
              type='number'
              error={errors.amount}
              rightAddon={
                props.currency ? (
                  <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                    {props.currency}
                  </span>
                ) : undefined
              }
            />
            <FormTextareaField name='comment' label='Comment' rows={3} maxLength={130} />
            <FormFieldLayout label='Linked Transaction'>
              <Button type='button' variant='outline' onClick={props.setLinkedTransaction}>
                {props.linkedTransaction?.referenceId ?? 'Select'}
              </Button>
            </FormFieldLayout>
          </DataCard>
        </DataColumn>
      </DataGrid>
      <div className='mt-4 flex justify-center'>
        <Button disabled={isSubmitting} type='submit'>
          Submit
        </Button>
      </div>
    </Form>
  )
}

interface OuterProps {
  client: ClientDto | null
  linkedTransaction: TransactionDto | null
  wallets: WalletDto[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  walletId: string
  directionTypes: TransactionDirectionType[]
  transactionTypes: TransactionTypeDto[]

  onSubmit(values: FormSubmitValues<ComplexTransfer>): Promise<void>
  setClient(): void
  setLinkedTransaction(): void
  setCurrency(currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string): void
  setWallet(walletId: string): void
}

export const AdjustmentWalletForm = withFormik<OuterProps, AdjustmentFormValues>({
  mapPropsToValues: ({ client, walletId, currency }) => ({
    client: client?.name || null,
    walletId,
    currency,
    amount: 0,
    comment: '',
    directionTypeId: '100',
    transactionTypeId: 610,
  }),
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AdjustmentFormValues> = {}

    if (!values.directionTypeId) {
      errors.directionTypeId = 'Required'
    }

    if (!values.transactionTypeId) {
      errors.transactionTypeId = 'Required'
    }

    if (!values.walletId) {
      errors.walletId = 'Required'
    }

    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }

    return errors
  },
  enableReinitialize: true,
})(AdjustmentWalletFormUI)
